import { useCartContext } from 'context/CartContext';
import { getSelectedVariants } from 'helpers/cartHelpers';
import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { useMenuContext } from 'context/MenuContext';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import '@reach/dialog/styles.css';
import { getTranslation } from 'dictionary';
import { useUserContext } from 'context/UserContext';
import { Order, OrderObjectCreation } from 'Types';
import createRandomKey from 'helpers/createRandomKey';
import Error from 'components/Error';
import OrderModalStep1 from './OrderModalStep1';
import OrderModalStep2 from './OrderModalStep2';
import OrderNavBar from 'components/NavBars/OrderNavBar';
import { animated, useSpring } from 'react-spring';

interface Props {
  onDismiss: () => void;
  showDialog: boolean;
}

const createOrderObject = (order: Order): OrderObjectCreation[] =>
  order.products.map((orderItem) => {
    const variants = getSelectedVariants(
      orderItem.product,
      orderItem.variantSelection
    );

    return {
      productPrice:
        orderItem.product.price_promotional || orderItem.product.price,
      discount: orderItem.product.price_promotional
        ? orderItem.product.price_promotional - orderItem.product.price
        : 0,
      productId: orderItem.product.id,
      quantity: orderItem.count,
      variantSelection: variants.map((variant) => ({
        groupId: variant.groupId,
        optionId: variant.id,
        optionPrice: variant.price,
      })),
    };
  });

const OrderModal: React.FC<Props> = ({ showDialog, onDismiss }) => {
  const { language } = useUserContext();

  const { currency_symbol, slug, whatsapp, features, cart } = useMenuContext();
  const additionalFields = cart?.additionalFields || [];
  const { state, dispatch } = useCartContext();
  const [savingOrder, setSavingOrder] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const AnimatedDialogOverlay = animated(DialogOverlay);
  const AnimatedDialogContent = animated(DialogContent);
  const { right } = useSpring({
    from: { right: '-100%' },
    right: showDialog ? '0' : '-100%',
  });

  const handleDismiss = () => {
    if (step === 1) return onDismiss();
    else setStep(1);
  };

  const changeCartQuantity = (
    index: number,
    action: 'INCREMENT' | 'DECREMENT'
  ) => {
    //@ts-ignore
    dispatch({
      type: 'change_product_quantity',
      payload: {
        index,
        type: action,
      },
    });
  };

  const removeProduct = (index: number) => {
    //@ts-ignore
    dispatch({
      type: 'remove_product',
      payload: { index },
    });
  };
  const notify = () =>
    toast.success(getTranslation('menu_order', 'ORDER_SENT', language));

  // We are pre creating the order id, so we can get the url we need for whatsapp. assuming the save of the order will be correct.
  // If something fails, the page here will return a 404, and if the user visits again the order modal, will se the error in screen.
  // Not the best approach, but safari doesn't allows us to trigger window.open in an async function.
  const orderId = createRandomKey();
  const handleSubmit = async (values: { [key: string]: any } = {}) => {
    if (features === 'BAR_FULL') {
      return createOrder();
    }
    const valuesAsString = Object.entries(values).map(([fieldId, value]) => ({
      fieldId,
      value: JSON.stringify(value),
    }));
    const url = `https://${window.location.hostname}/${slug}/order/${orderId}`;
    const text =
      getTranslation('menu_order', 'WHATSAPP_MESSAGE', language) + '\n' + url;
    const whatsappMessage = `https://wa.me/${whatsapp}?text=${text}`;
    createOrder(valuesAsString);
    setStep(1);
    setTimeout(() => {
      window.open(whatsappMessage);
    }, 800);
  };

  const createOrder = async (values?: { fieldId: string; value: string }[]) => {
    try {
      setSavingOrder(true);
      const order = createOrderObject(state);
      const response = await fetch('/api/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account: slug,
          order,
          orderId,
          additionalData: values || null,
        }),
      });
      const data = await response.json();
      if (response.status !== 201) return setError(data.error);
      //@ts-ignore
      dispatch({ type: 'clean_cart' });
      onDismiss();
      notify();
    } catch (error: any) {
      console.log('ERROR', error);
      setError(error.message);
    } finally {
      setSavingOrder(false);
    }
  };
  const handleCloseModal = () => {
    setStep(1);
    onDismiss();
  };

  const shouldSkipStep2 = !additionalFields.length || features === 'BAR_FULL';

  return (
    <AnimatedDialogOverlay
      isOpen={showDialog}
      onDismiss={handleCloseModal}
      style={{ zIndex: 30 }}
    >
      <AnimatedDialogContent
        aria-label="orderModal"
        style={{
          width: '100%',
          maxWidth: '400px',
          height: '100vh',
          padding: 0,
          paddingBottom: 10,
          margin: 0,
          position: 'fixed',
          top: 0,
          right: right,
        }}
      >
        <Error errorMessage={error}>
          <div className="bg-white h-full pb-[76px]">
            <OrderNavBar onDismiss={onDismiss} />
            <div className="overflow-y-scroll h-full">
              {
                //@ts-ignore
                state.products.length === 0 ? (
                  <div className="px-10 py-32 text-center">
                    {getTranslation('menu_order', 'EMPTY_CART', language)}
                  </div>
                ) : step === 1 ? (
                  <OrderModalStep1
                    state={state}
                    onRemove={removeProduct}
                    currency_symbol={currency_symbol}
                    language={language}
                    changeCartQuantity={changeCartQuantity}
                    features={features}
                    savingOrder={savingOrder}
                    skipStep2={shouldSkipStep2}
                    onConfirm={() => {
                      if (shouldSkipStep2) handleSubmit();
                      else setStep(2);
                    }}
                  />
                ) : (
                  <OrderModalStep2
                    savingOrder={savingOrder}
                    language={language}
                    onSubmit={handleSubmit}
                  />
                )
              }
            </div>
          </div>
        </Error>
      </AnimatedDialogContent>
    </AnimatedDialogOverlay>
  );
};

export default OrderModal;
